import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { usePageState } from "@/contexts/NavigationContext";
import { CaretLeftIcon, CaretRightIcon, MagnifyingGlassIcon, XIcon, CircleNotchIcon } from "@phosphor-icons/react";

/**
 * Consulta común de VENTAS Y FACTURACIÓN (FACTURAS, NOTAS DE DÉBITO, NOTAS DE CRÉDITO):
 * POR MES · RANGO DE FECHAS · MOSTRAR TODO, búsqueda por cliente o número y paginación.
 * Los documentos se ubican por su FECHA DE EMISIÓN (`fecha_emision`, calculada en el servidor).
 */
export const SIN_DOCUMENTOS = "NO HAY DOCUMENTOS EN EL PERÍODO SELECCIONADO";
const MESES = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE",
  "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"];
const MODOS = [["mes", "POR MES"], ["rango", "RANGO DE FECHAS"], ["todo", "MOSTRAR TODO"]];

const mesActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const moverMes = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const finDeMes = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
};
const dma = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
export const nombreMes = (ym) => {
  const [y, m] = (ym || mesActual()).split("-").map(Number);
  return `${MESES[m - 1]} ${y}`;
};

/** Fecha de emisión (AAAA-MM-DD, ya local) → "16 ene 2025", sin desfase de zona horaria. */
export const fmtEmision = (f) => {
  if (!f) return "—";
  const [y, m, d] = String(f).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return f;
  return new Date(y, m - 1, d).toLocaleDateString("es-HN", { day: "2-digit", month: "short", year: "numeric" });
};

export function usePeriodQuery(seccion, filtros = {}) {
  const [periodo, setPeriodo] = usePageState(`periodo-${seccion}`, { modo: "mes", mes: mesActual(), desde: "", hasta: "" });
  const [buscar, setBuscar] = useState("");
  const [buscarDeb, setBuscarDeb] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const pedido = useRef(0);

  useEffect(() => { const t = setTimeout(() => setBuscarDeb(buscar.trim()), 300); return () => clearTimeout(t); }, [buscar]);

  // Solo los filtros del modo ACTIVO viajan al servidor (sin filtros de fecha ocultos)
  let desde = "", hasta = "", rangoError = "";
  if (periodo.modo === "mes") { desde = `${periodo.mes}-01`; hasta = finDeMes(periodo.mes); }
  else if (periodo.modo === "rango") {
    if (!periodo.desde || !periodo.hasta) rangoError = "Selecciona las fechas DESDE y HASTA.";
    else if (periodo.desde > periodo.hasta) rangoError = "La fecha DESDE no puede ser posterior a la fecha HASTA.";
    else { desde = periodo.desde; hasta = periodo.hasta; }
  }
  const claveFiltros = JSON.stringify(filtros);
  const clave = `${seccion}|${desde}|${hasta}|${buscarDeb}|${claveFiltros}|${periodo.modo}`;
  const claveAnterior = useRef(clave);

  const cargar = async (pg = page) => {
    if (rangoError) { setData(null); setLoading(false); return; }
    const n = ++pedido.current;
    setLoading(true); setError("");
    try {
      const params = { page: pg, page_size: 50, ...filtros };
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      if (buscarDeb) params.buscar = buscarDeb;
      Object.keys(params).forEach((k) => (params[k] === "" || params[k] == null) && delete params[k]);
      const { data: d } = await api.get(`/sales/consulta/${seccion}`, { params });
      if (n === pedido.current) setData(d);
    } catch (e) { if (n === pedido.current) { setError(apiErr(e)); setData(null); } }
    finally { if (n === pedido.current) setLoading(false); }
  };

  useEffect(() => {
    // Cambiar cualquier filtro vuelve a la página 1
    if (claveAnterior.current !== clave) { claveAnterior.current = clave; if (page !== 1) { setPage(1); return; } }
    cargar(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, page]);

  const etiqueta = periodo.modo === "mes" ? nombreMes(periodo.mes)
    : periodo.modo === "rango" ? (rangoError ? "RANGO INCOMPLETO" : `DEL ${dma(desde)} AL ${dma(hasta)}`)
    : "TODOS LOS DOCUMENTOS";
  return {
    periodo, setPeriodo, buscar, setBuscar, page, setPage, data, loading, error, rangoError, etiqueta,
    buscando: buscar.trim() !== buscarDeb || (loading && Boolean(buscarDeb)),
    // Mensaje cuando no hay filas: distingue «sin resultados de la búsqueda» de «período sin documentos»
    vacio: buscarDeb ? `SIN RESULTADOS PARA «${buscarDeb}» EN EL PERÍODO SELECCIONADO` : SIN_DOCUMENTOS,
    items: data?.items || [], totales: data?.totales || null, reload: () => cargar(page),
  };
}

export function PeriodToolbar({ pq, placeholder = "BUSCAR POR CLIENTE O NÚMERO DE DOCUMENTO…", children, saldo = true, testid = "period" }) {
  const { periodo, setPeriodo, totales } = pq;
  const set = (patch) => setPeriodo({ ...periodo, ...patch });
  const modoLabel = MODOS.find(([v]) => v === periodo.modo)?.[1];
  return (
    <div className="bg-card border border-border/60 rounded-lg" data-testid={`${testid}-toolbar`}>
      <div className="p-3 flex gap-3 flex-wrap items-center">
        <div className="inline-flex rounded-md border border-border overflow-hidden" role="group">
          {MODOS.map(([v, l]) => (
            <button key={v} onClick={() => set({ modo: v })} data-testid={`${testid}-modo-${v}`}
              className={`px-3 py-1.5 text-[11px] font-semibold tracking-wider ${periodo.modo === v
                ? "bg-agro-green/15 text-agro-green" : "text-text-muted hover:text-text-primary"}`}>{l}</button>
          ))}
        </div>
        {periodo.modo === "mes" && (
          <div className="flex items-center gap-1" data-testid={`${testid}-mes`}>
            <button onClick={() => set({ mes: moverMes(periodo.mes, -1) })} title="MES ANTERIOR" aria-label="MES ANTERIOR"
              data-testid={`${testid}-mes-anterior`} className="p-1.5 rounded border border-border text-text-secondary hover:text-agro-green">
              <CaretLeftIcon size={14} weight="bold" /></button>
            <span className="min-w-[150px] text-center text-sm font-semibold" data-testid={`${testid}-mes-label`}>{nombreMes(periodo.mes)}</span>
            <button onClick={() => set({ mes: moverMes(periodo.mes, 1) })} title="MES SIGUIENTE" aria-label="MES SIGUIENTE"
              data-testid={`${testid}-mes-siguiente`} className="p-1.5 rounded border border-border text-text-secondary hover:text-agro-green">
              <CaretRightIcon size={14} weight="bold" /></button>
          </div>
        )}
        {periodo.modo === "rango" && (
          <div className="flex items-center gap-2 text-[11px] font-semibold text-text-muted">
            DESDE <input type="date" value={periodo.desde} max={periodo.hasta || undefined} onChange={(e) => set({ desde: e.target.value })}
              data-testid={`${testid}-desde`} className="bg-app border border-border rounded-md px-2 py-1.5 text-sm text-text-primary" />
            HASTA <input type="date" value={periodo.hasta} min={periodo.desde || undefined} onChange={(e) => set({ hasta: e.target.value })}
              data-testid={`${testid}-hasta`} className="bg-app border border-border rounded-md px-2 py-1.5 text-sm text-text-primary" />
          </div>
        )}
        <label className="relative flex-1 min-w-[220px]">
          <span className="sr-only">Buscar</span>
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden="true" />
          <input type="search" value={pq.buscar} onChange={(e) => pq.setBuscar(e.target.value)} placeholder={placeholder} data-testid={`${testid}-buscar`}
            autoComplete="off" className="w-full min-h-[40px] bg-app border border-border rounded-md pl-8 pr-9 py-2 text-sm" />
          {pq.buscar && (
            <button type="button" onClick={() => pq.setBuscar("")} aria-label="Limpiar búsqueda" data-testid={`${testid}-buscar-limpiar`}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded text-text-muted hover:text-text-primary"><XIcon size={14} /></button>
          )}
        </label>
        {pq.buscar.trim() && (
          <span className="text-xs text-text-muted whitespace-nowrap flex items-center gap-1" aria-live="polite" data-testid={`${testid}-buscar-conteo`}>
            {pq.buscando ? <><CircleNotchIcon size={12} className="animate-spin" /> Buscando…</>
              : totales ? (totales.cantidad ? `${totales.cantidad} coincidencias` : "Sin resultados") : ""}
          </span>
        )}
        {children}
      </div>
      <div className="px-3 py-2 border-t border-border/50 flex flex-wrap gap-x-4 gap-y-1 text-[11px] tracking-wider text-text-muted" data-testid={`${testid}-resumen`}>
        <span>MODO: <strong className="text-text-primary">{modoLabel}</strong></span>
        <span>PERÍODO: <strong className="text-text-primary" data-testid={`${testid}-etiqueta`}>{pq.etiqueta}</strong></span>
        {totales && <>
          <span>DOCUMENTOS: <strong className="text-text-primary" data-testid={`${testid}-cantidad`}>{totales.cantidad}</strong>
            {totales.anuladas ? ` (${totales.anuladas} ANULADOS)` : ""}</span>
          <span>TOTAL: <strong className="text-agro-green" data-testid={`${testid}-total`}>{fmtL(totales.total)}</strong></span>
          {saldo && totales.saldo != null && <span>SALDO PENDIENTE: <strong className="text-agro-yellow" data-testid={`${testid}-saldo`}>{fmtL(totales.saldo)}</strong></span>}
        </>}
        {pq.rangoError && <span className="text-red-400" data-testid={`${testid}-rango-error`}>{pq.rangoError}</span>}
        {pq.error && <span className="text-red-400">{pq.error}</span>}
      </div>
    </div>
  );
}

export function Pager({ pq, testid = "period" }) {
  const d = pq.data;
  if (!d || d.paginas <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-border/50 text-[11px] tracking-wider text-text-muted" data-testid={`${testid}-pager`}>
      <button disabled={d.page <= 1} onClick={() => pq.setPage(d.page - 1)} className="px-2 py-1 border border-border rounded disabled:opacity-30">ANTERIOR</button>
      <span>PÁGINA {d.page} DE {d.paginas}</span>
      <button disabled={d.page >= d.paginas} onClick={() => pq.setPage(d.page + 1)} data-testid={`${testid}-pager-next`}
        className="px-2 py-1 border border-border rounded disabled:opacity-30">SIGUIENTE</button>
    </div>
  );
}
